// lib.
import { useState } from 'react';
import { Dropdown, DropdownButton } from 'react-bootstrap';

// endpoint.
import {
  callOrchestratorPricing,
  callRejectedOrchestratorPricing,
  callRescueOrchestratorPricing,
} from '../../../../apis/orchestratorPricing/orchestratorPricing';

// types.
import { RETRY_POLICIES, RetryPolicy } from './retryPolicies';

// components.
import { RetryConfirmModal } from './RetryConfirmModal';

interface Props {
  nid: string;
  email: string;
  taskType?: string;
  status?: string;
  isRetry?: boolean;
}

export enum BusinessType {
  COMMITTEE = 'committee',
}
export enum RetrySource {
  IDM_APROBOLOGIA = 'idm aprobologia',
}

export const DropdownPolicies = ({ nid, email, taskType, status }: Props) => {
  const role = localStorage.getItem('ROL');

  const [selectedPolicy, setSelectedPolicy] = useState<RetryPolicy | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handlePolicySelect = (policy: RetryPolicy) => {
    setSelectedPolicy(policy);
    setShowModal(true);
  };

  const handleRetry = async (comment: string) => {
    if (!selectedPolicy) return;

    const isRejection =
      selectedPolicy.label === 'Rechazo por oportunidad de negocio';
    const isRescue = selectedPolicy.isRescue;

    try {
      setIsLoading(true);
      if (isRejection) {
        await callRejectedOrchestratorPricing({
          nid,
          email,
          genericRejectedReason: 'oportunidad de negocio hs',
          eventName: RetrySource.IDM_APROBOLOGIA,
          comment,
        });
      } else if (isRescue) {
        await callRescueOrchestratorPricing({ nid, comment });
      } else {
        await callOrchestratorPricing({
          nid,
          email,
          taskType: selectedPolicy.taskType ?? taskType,
          status,
          eventName: selectedPolicy.eventName,
          isRetry: true,
          retrySourceName: RetrySource.IDM_APROBOLOGIA,
          comment,
          businessType: BusinessType.COMMITTEE,
        });
      }
      setShowModal(false);
      setFeedback({
        type: 'success',
        message: isRejection
          ? 'Rechazo enviado correctamente.'
          : 'Reintento enviado correctamente.',
      });
    } catch {
      setFeedback({
        type: 'error',
        message: isRejection
          ? 'Error al enviar el rechazo.'
          : 'Error al enviar el reintento.',
      });
    } finally {
      setIsLoading(false);
      setSelectedPolicy(null);
    }
  };

  if (!nid || role === 'comercial') {
    return null;
  }

  return (
    <div className="d-flex gap-3 align-items-center">
      <DropdownButton variant="secondary" title="Soporte">
        {RETRY_POLICIES.map(policy => (
          <Dropdown.Item
            key={policy.eventName}
            onClick={() => handlePolicySelect(policy)}
          >
            {policy.label}
          </Dropdown.Item>
        ))}
      </DropdownButton>
      {selectedPolicy && (
        <RetryConfirmModal
          description={selectedPolicy.description}
          show={showModal}
          onClose={() => setShowModal(false)}
          onConfirm={handleRetry}
          title={selectedPolicy.label}
          onReset={() => setFeedback(null)}
          isLoading={isLoading}
          defaultComment={selectedPolicy.defaultComment}
          labelCopy={
            selectedPolicy.label === 'Rechazo por oportunidad de negocio'
              ? 'Razón para el rechazo'
              : 'Razón para el reintento'
          }
          authorizerOptions={selectedPolicy.rescueAuthorizers}
          authorizerLabel={selectedPolicy.rescueAuthorizerLabel}
        />
      )}
      {feedback && (
        <span
          style={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: feedback.type === 'success' ? 'green' : 'red',
          }}
        >
          {feedback.message}
        </span>
      )}
    </div>
  );
};
